import type { ClientSession } from "mongoose"
import type {
  BillingStorage,
  BillingStorageTransaction,
  PersistedSubscriptionEvent,
  StoredSubscriptionProjection,
  SubscriptionEvent,
} from "@rpcbase/billing"

import { ZRBTenantSubscriptionEvent } from "./models/RBTenantSubscriptionEvent"
import type { LoadModelCtx } from "./modelsApi"
import { models } from "./modelsApi"
import { withTransaction } from "./transactions"


type MongoBillingTransaction = {
  session: ClientSession
}

type SubscriptionEventDocument = Record<string, unknown> & {
  _id?: unknown
}

const getTransactionSession = (transaction?: BillingStorageTransaction): ClientSession | undefined => {
  if (!transaction || typeof transaction !== "object" || !("session" in transaction)) return undefined
  return (transaction as MongoBillingTransaction).session
}

const toSubscriptionEvent = (document: SubscriptionEventDocument): PersistedSubscriptionEvent => {
  const parsed = ZRBTenantSubscriptionEvent.parse(document)
  if (!document._id) throw new Error("Persisted subscription event is missing its id")
  return {
    ...parsed,
    id: String(document._id),
  }
}

const getExistingEventQuery = (event: SubscriptionEvent, tenantId: string): Record<string, unknown> => ({
  tenantId,
  idempotencyKey: event.idempotencyKey,
})

const isDuplicateKeyError = (error: unknown): boolean =>
  Boolean(error && typeof error === "object" && "code" in error && error.code === 11000)

const toProjectionDocument = (projection: StoredSubscriptionProjection): Record<string, unknown> => ({
  tenantId: projection.tenantId,
  subscriptionId: projection.subscriptionId,
  type: projection.type,
  parentSubscriptionId: projection.parentSubscriptionId ?? undefined,
  scope: projection.scope,
  scopeId: projection.scopeId ?? undefined,
  planKey: projection.planKey,
  priceId: projection.priceId ?? undefined,
  status: projection.status,
  intervalUnit: projection.intervalUnit,
  intervalCount: projection.intervalCount,
  modules: projection.modules,
  billingAnchor: projection.billingAnchor,
  currentPeriodStart: projection.currentPeriodStart,
  currentPeriodEnd: projection.currentPeriodEnd,
  cancelAtPeriodEnd: projection.cancelAtPeriodEnd,
  cancelAt: projection.cancelAt ?? undefined,
  canceledAt: projection.canceledAt ?? undefined,
  scheduledPlanKey: projection.scheduledPlan?.planKey,
  scheduledPlanEffectiveAt: projection.scheduledPlan?.effectiveAt,
  provider: projection.provider ?? undefined,
  latestEventId: projection.latestEventId,
  latestEventAt: projection.latestEventAt,
  projectedAt: projection.projectedAt,
})

export const createTenantBillingStorage = (params: {
  ctx: LoadModelCtx
  tenantId: string
}): BillingStorage => {
  const findExistingEvent = async (
    query: Record<string, unknown>,
    transaction?: BillingStorageTransaction,
  ): Promise<PersistedSubscriptionEvent | null> => {
    const EventModel = await models.get("RBTenantSubscriptionEvent", params.ctx)
    const request = EventModel.findOne(query)
    const session = getTransactionSession(transaction)
    if (session) request.session(session)
    const document = await request.lean() as SubscriptionEventDocument | null
    return document ? toSubscriptionEvent(document) : null
  }

  return {
    runInTransaction: (operation) => withTransaction(
      { ctx: params.ctx },
      ({ session }) => operation({ session } satisfies MongoBillingTransaction),
    ),
    createEvent: async (event, transaction) => {
      if (event.tenantId !== params.tenantId) {
        throw new Error("Billing event tenantId does not match the storage tenant")
      }
      const existingQuery = getExistingEventQuery(event, params.tenantId)
      const existing = await findExistingEvent(existingQuery, transaction)
      if (existing) return existing

      const EventModel = await models.get("RBTenantSubscriptionEvent", params.ctx)
      const eventDocument = ZRBTenantSubscriptionEvent.parse(event)
      const session = getTransactionSession(transaction)

      try {
        const documents = await EventModel.create([eventDocument], session ? { session } : undefined)
        const document = documents[0]?.toObject() as SubscriptionEventDocument | undefined
        if (!document) throw new Error("Subscription event was not persisted")
        return toSubscriptionEvent(document)
      } catch (error) {
        if (!isDuplicateKeyError(error)) throw error
        const duplicate = await findExistingEvent(existingQuery, transaction)
        if (!duplicate) throw error
        return duplicate
      }
    },
    listEvents: async (tenantId, subscriptionId, transaction) => {
      if (tenantId !== params.tenantId) throw new Error("Billing storage tenant mismatch")
      const EventModel = await models.get("RBTenantSubscriptionEvent", params.ctx)
      const request = EventModel.find({ tenantId, subscriptionId })
        .sort({ effectiveAt: 1, occurredAt: 1, _id: 1 })
      const session = getTransactionSession(transaction)
      if (session) request.session(session)
      const documents = await request.lean() as SubscriptionEventDocument[]
      return documents.map(toSubscriptionEvent)
    },
    upsertProjection: async (projection, transaction) => {
      if (projection.tenantId !== params.tenantId) throw new Error("Billing projection tenant mismatch")
      const ProjectionModel = await models.get("RBTenantSubscriptionProjection", params.ctx)
      const session = getTransactionSession(transaction)
      await ProjectionModel.replaceOne(
        {
          tenantId: params.tenantId,
          subscriptionId: projection.subscriptionId,
        },
        toProjectionDocument(projection),
        {
          upsert: true,
          runValidators: true,
          ...(session ? { session } : {}),
        },
      )
    },
  }
}
